import React from 'react';

export type BadgeVariant = 'success' | 'error' | 'warning' | 'info' | 'purple' | 'default';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  /** Badge content */
  children: React.ReactNode;
  /** Visual variant of the badge */
  variant?: BadgeVariant;
  /** Size of the badge */
  size?: BadgeSize;
  /** Custom styles */
  style?: React.CSSProperties;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Badge Atom Component
 * 
 * A small status descriptor for UI elements.
 * 
 * @example
 * ```tsx
 * // Success badge
 * <Badge variant="success">Active</Badge>
 * 
 * // Custom styled badge
 * <Badge variant="info" style={{ backgroundColor: '#custom' }}>Custom</Badge>
 * ```
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'sm',
  style,
  className = '',
}) => {
  const baseClasses = 'inline-flex items-center font-medium rounded-full';
  
  const variantClasses = {
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    warning: 'bg-yellow-100 text-yellow-800',
    info: 'bg-blue-100 text-blue-800',
    purple: 'bg-purple-100 text-purple-800',
    default: 'bg-gray-100 text-gray-800',
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-2.5 py-1.5 text-sm',
  };

  const classes = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    className,
  ].filter(Boolean).join(' ');

  return (
    <span className={classes} style={style}>
      {children}
    </span>
  );
};
